import {Button, ButtonColor, ButtonSize, ButtonType} from '@atoms/LinksAndButtons/Button';
import {Container} from '@atoms/Layout/Container';
import {MainGrid} from '@atoms/Layout/MainGrid';
import {P} from '@atoms/Text/P';
import {AreaCard} from '@molecules/ResidentialCards/ResidenceCardsOld/AreaCard';
import {SearchService} from 'features/search/services/SearchService';
import {SITE_URLS} from 'features/search/constants';
import {DisplayOptionSearchResultBlock} from 'features/search/enums';
import {buildFiltersFromUrl, convertFiltersForApi, defaultStageAttributes} from 'features/search/helpers';
import {ApiFilters, SearchQuery, SearchResponse, SearchResult, Tag} from 'features/search/types';
import {useDeviceStore, selectIsMobile} from 'features/ui/store/useDeviceStore';
import React, {useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {ObjectStatus, SortBy} from 'features/search/filters/enums';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {IContentBlock} from 'features/blocks/types';
import {AccessibleSlider} from '@molecules/SlidingContent/AccessibleSlider';

import 'components/blocks/ProjectAndObject/SearchResultBlockV2/style.scss';
import {gtmEventsSliderClick} from 'features/services/googleTagManager/events';

import {useAuth} from "features/authentication/hooks/useAuth";
import { ResidenceCardType, ResidenceCard } from '@molecules/ResidentialCards/ResidenceCard';
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

interface SearchResultBlockType {
    searchTypeArea: boolean;
    searchTypeStage: boolean;
    searchTypeObject: boolean;
    searchUrl: string;
    searchLink: string;
    buttonText: string;
    title: string;
    content: string;
    displayOption: DisplayOptionSearchResultBlock;
    onStartPage?: boolean;
}

const FOLDED_COUNT = 9;

type Props = IContentBlock<SearchResultBlockType>;

export const SearchResultBlockV2: React.FunctionComponent<Props> = (props) => {

    const isMobile = useDeviceStore(selectIsMobile);

    const [searchResult, setSearchResult] = useState<SearchResult[]>([]);
    const [isFolded, setIsFolded] = useState<boolean>(true);

    const {user} = useAuth();

    useEffect(() => {
        getSearchResult();
    }, [user]);

    const getSearchResult = async () => {
        if(!props.data.searchUrl) return;

        const filters = buildFiltersFromUrl(props.data.searchUrl);

        if(filters && filters.tags) {
            const tagUrls = filters.tags.map(x => x.url).filter((url): url is string => !!url);

            if(tagUrls.length > 0) {
                const tagResponse = await SearchService.searchTagsByUrls(tagUrls);
                filters.tags = tagResponse.data?.hits ?? [];
            }
        }

        const apiFilters = convertFiltersForApi(filters);
        const searchQuery = {} as SearchQuery<ApiFilters>;

        searchQuery.filter = apiFilters;

        if(props.data.onStartPage) {
            searchQuery.filter.hideAsMoveInReady = false;
            searchQuery.filter.objectStatus = [ObjectStatus.FOR_SALE];
            searchQuery.sort = ['sortOrder:asc', "inhabitDateTimeStamp:asc"];
            searchQuery.limit = 10;
            searchQuery.attributesToRetrieve = defaultStageAttributes;
        }

        if(props.data.searchTypeStage) {
            const response = await SearchService.fetchStages(searchQuery, props.data.onStartPage);

            if(response.data && response.data.hits && response.data.hits.length > 0) {
                setSearchResult(response.data.hits);
            }

        }

        if(props.data.searchTypeObject) {
            const response = await SearchService.fetchObjects(searchQuery.filter, SortBy.ACCESS_ASC, 0, 10);

            if(response.data && response.data.hits && response.data.hits.length > 0) {
                setSearchResult(response.data.hits);
            }
        }
    };

    const _getSearchResultObjects = () => {};

    const getSearchUrl = () => {
        if(!props.data.searchUrl) return SITE_URLS.searchUrl;

        const url = new URL(props.data.searchUrl);

        return url.pathname + url.search;
    };

    // Kortets rubriknivå ska ligga ett steg under blockets rubrik: LIST och SLIDER-på-startsidan
    // har en h2-rubrik (korten -> h3), övriga slider-fall en h3-rubrik (korten -> h4, default).
    const cardLevel = (props.data.displayOption === DisplayOptionSearchResultBlock.LIST || props.data.onStartPage)
        ? HeadlineLevel.H3
        : HeadlineLevel.H4;

    const renderResultCard = (searchResult: SearchResult, solo: boolean) => {
        switch(searchResult.type) {
            case 'object':
                return <ResidenceCard 
                    residence={searchResult} 
                    cardType={ResidenceCardType.OBJECT}
                    level={cardLevel}
                    key={`card-${searchResult.id}`}
                    onClick={() => gtmEventsSliderClick("object", `${searchResult.stageName}: ${searchResult.name}`)}
                    horizontal={solo && !isMobile}
                />
            case 'area':
            case 'municipality':
            case 'county':
                return <AreaCard 
                    onClick={() => gtmEventsSliderClick("area", searchResult.name)} 
                    residence={searchResult} 
                    key={`card-${searchResult.id}`} 
                />;
            default:
                return <ResidenceCard 
                    residence={searchResult} 
                    cardType={ResidenceCardType.PROJECT}
                    level={cardLevel}
                    key={`card-${searchResult.id}`}
                    onClick={() => gtmEventsSliderClick("stage", searchResult.name)}
                    horizontal={solo && !isMobile} 
                />
        }
    };

    const showMore = () => {
        setIsFolded(false);
    };

    const renderButtonAmount = () => {
        return searchResult.length - FOLDED_COUNT;
    };

    if(searchResult.length === 0) return null;

    return (
        <div className="search-result-block" id={props.id}>
            {props.data.displayOption === DisplayOptionSearchResultBlock.SLIDER &&
                <AccessibleSlider
                    level={props.data.onStartPage ? HeadlineLevel.H2 : HeadlineLevel.H3}
                    header={props.data.title}
                    text={props.data.content}
                    link={props.data.searchLink ?
                        props.data.searchLink :
                        getSearchUrl()}
                    linkText={props.data.buttonText ?
                        props.data.buttonText :
                        getFormattedMessage("jm.components.SuggestionSlider.seeAll")}
                >
                    {searchResult.map((item : SearchResult) => (
                        renderResultCard(item, searchResult.length === 1)
                    ))}
                </AccessibleSlider>
            }
            {props.data.displayOption === DisplayOptionSearchResultBlock.LIST &&
                <Container>
                    <Headline level={HeadlineLevel.H2} center>{props.data.title}</Headline>

                    <P center>
                        {props.data.content}
                    </P>

                    <MainGrid fullwidth>
                        {props.data.searchUrl &&
                            searchResult.slice(0, isFolded? FOLDED_COUNT : searchResult.length).map((result: SearchResult, _id) => {
                                return renderResultCard(result, false);
                            })
                        }

                        {isFolded && searchResult.length > 9 &&
                            <div className="selected-projects-block__show-more" >
                                <Button 
                                    onClick={showMore} 
                                    buttonColor={ButtonColor.DEFAULT} 
                                    buttonSize={ButtonSize.DEFAULT} 
                                    buttonType={ButtonType.DEFAULT}
                                >
                                    <FormattedMessage id="jm.components.SelectedProjectsBlock.showAll" /> ({renderButtonAmount()}) +</Button>
                            </div>
                        }
                    </MainGrid>
                </Container>
            }
        </div>
    )
};

